import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import jobProspectService from '@/services/jobProspectService'
import type { ListProspectsParams, AddProspectRequest } from '@/types'

export const jobProspectsKeys = {
  all: ['jobProspects'] as const,
  list: (params: ListProspectsParams) =>
    [
      ...jobProspectsKeys.all,
      'list',
      params.recruiterJobId,
      params.source,
      params.offerStatus,
      params.page,
    ] as const,
  detail: (id: string) => [...jobProspectsKeys.all, 'detail', id] as const,
}

export function useJobProspects(params: ListProspectsParams, enabled: boolean = true) {
  return useQuery({
    queryKey: jobProspectsKeys.list(params),
    queryFn: () => jobProspectService.listProspects(params),
    enabled: enabled && !!params.recruiterJobId,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: 2,
  })
}

export function useAddProspect() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: AddProspectRequest) => jobProspectService.addProspect(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobProspectsKeys.all })
      queryClient.invalidateQueries({ queryKey: ['recruiter-jobs'] })
    },
  })
}

export function useBulkAddProspects() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Parameters<typeof jobProspectService.bulkAddProspects>[0]) =>
      jobProspectService.bulkAddProspects(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobProspectsKeys.all })
      queryClient.invalidateQueries({ queryKey: ['recruiter-jobs'] })
    },
  })
}

export function useUpdateProspect() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { notes?: string } }) =>
      jobProspectService.updateProspect(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: jobProspectsKeys.all })
      queryClient.invalidateQueries({ queryKey: jobProspectsKeys.detail(id) })
    },
  })
}

export function useRemoveProspect() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => jobProspectService.removeProspect(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobProspectsKeys.all })
      queryClient.invalidateQueries({ queryKey: ['recruiter-jobs'] })
    },
  })
}
